const SCORED_FIELD_COUNT = 40;

const isPresent = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return false;
  }
  return true;
};

const isRemainingOopmPresent = (value: unknown): boolean => {
  if (!isPresent(value)) {
    return false;
  }
  if (value === -1 || value === '-1') {
    return false;
  }
  return true;
};

const classifications = (vob: any, newKey: string, oldKey: string): any[] => {
  const value = vob?.[newKey] ?? vob?.[oldKey];
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (cls) => cls && typeof cls === 'object' && !Array.isArray(cls)
  );
};

const anyClass = (list: any[], predicate: (cls: any) => boolean): boolean =>
  list.some(predicate);

export type CompletenessResult = {
  compScore: number;
  missingFields: string | null;
};

/**
 * Completeness score 0-100 matching vob-service / VOB Quality report:
 * 40 equally weighted fields; 0 counts as present; remaining OOPM of -1 does not.
 * Service-level deductible and OOPM are not scored.
 */
export const calculateCompleteness = (vob: any = {}): CompletenessResult => {
  const payorId = vob?.payor?.payorId ?? vob?.payer?.payorId;
  const payorPlanId = vob?.plan?.payorPlanId;

  const innCls = classifications(
    vob,
    'innClassifications',
    'inNetwVobClassifications'
  );
  const oonCls = classifications(
    vob,
    'oonClassifications',
    'ooNetwVobClassifications'
  );

  const fields: { label: string; present: boolean }[] = [
    { label: 'Payer', present: isPresent(payorId) },
    { label: 'Plan', present: isPresent(payorPlanId) },
    { label: 'Policy Begin Date', present: isPresent(vob?.policyBeginDate) },
    { label: 'Policy End Date', present: isPresent(vob?.policyEndDate) },

    {
      label: 'INN Deductible',
      present:
        isPresent(vob?.innDeductible?.total) || isPresent(vob?.inNetwDeductible),
    },
    {
      label: 'INN Remaining Deductible',
      present:
        isPresent(vob?.innDeductible?.remaining) ||
        isPresent(vob?.inNetwDeductibleRemaining),
    },
    {
      label: 'INN OOPM',
      present: isPresent(vob?.innOopm?.total) || isPresent(vob?.inNetwOopMax),
    },
    {
      label: 'INN Remaining OOPM',
      present:
        isRemainingOopmPresent(vob?.innOopm?.remaining) ||
        isRemainingOopmPresent(vob?.inNetwOopMaxRemaining),
    },
    { label: 'INN Coinsurance', present: isPresent(vob?.innCoInsurance) },
    { label: 'INN Copay', present: isPresent(vob?.innCoPay) },
    {
      label: 'INN Family Deductible',
      present:
        isPresent(vob?.innFamilyDeductible?.total) ||
        isPresent(vob?.inNetwFamilyDeductible),
    },
    {
      label: 'INN Family Remaining Deductible',
      present:
        isPresent(vob?.innFamilyDeductible?.remaining) ||
        isPresent(vob?.inNetwFamilyDeductibleRemaining),
    },
    {
      label: 'INN Family OOPM',
      present:
        isPresent(vob?.innFamilyOopm?.total) ||
        isPresent(vob?.inNetwFamilyOopMax),
    },
    {
      label: 'INN Family Remaining OOPM',
      present:
        isRemainingOopmPresent(vob?.innFamilyOopm?.remaining) ||
        isRemainingOopmPresent(vob?.inNetwFamilyOopMaxRemaining),
    },
    {
      label: 'INN Family Coinsurance',
      present: isPresent(vob?.innFamilyCoInsurance),
    },
    { label: 'INN Family Copay', present: isPresent(vob?.innFamilyCoPay) },

    {
      label: 'OON Deductible',
      present:
        isPresent(vob?.oonDeductible?.total) || isPresent(vob?.ooNetwDeductible),
    },
    {
      label: 'OON Remaining Deductible',
      present:
        isPresent(vob?.oonDeductible?.remaining) ||
        isPresent(vob?.ooNetwDeductibleRemaining),
    },
    {
      label: 'OON OOPM',
      present: isPresent(vob?.oonOopm?.total) || isPresent(vob?.ooNetwOopMax),
    },
    {
      label: 'OON Remaining OOPM',
      present:
        isRemainingOopmPresent(vob?.oonOopm?.remaining) ||
        isRemainingOopmPresent(vob?.ooNetwOopMaxRemaining),
    },
    { label: 'OON Coinsurance', present: isPresent(vob?.oonCoInsurance) },
    { label: 'OON Copay', present: isPresent(vob?.oonCoPay) },
    {
      label: 'OON Family Deductible',
      present:
        isPresent(vob?.oonFamilyDeductible?.total) ||
        isPresent(vob?.ooNetwFamilyDeductible),
    },
    {
      label: 'OON Family Remaining Deductible',
      present:
        isPresent(vob?.oonFamilyDeductible?.remaining) ||
        isPresent(vob?.ooNetwFamilyDeductibleRemaining),
    },
    {
      label: 'OON Family OOPM',
      present:
        isPresent(vob?.oonFamilyOopm?.total) ||
        isPresent(vob?.ooNetwFamilyOopMax),
    },
    {
      label: 'OON Family Remaining OOPM',
      present:
        isRemainingOopmPresent(vob?.oonFamilyOopm?.remaining) ||
        isRemainingOopmPresent(vob?.ooNetwFamilyOopMaxRemaining),
    },
    {
      label: 'OON Family Coinsurance',
      present: isPresent(vob?.oonFamilyCoInsurance),
    },
    { label: 'OON Family Copay', present: isPresent(vob?.oonFamilyCoPay) },

    {
      label: 'INN SL Copay',
      present: anyClass(
        innCls,
        (cls) => isPresent(cls?.coPay) || isPresent(cls?.perDayVisit)
      ),
    },
    {
      label: 'INN SL Max Days',
      present: anyClass(innCls, (cls) => isPresent(cls?.maxDays)),
    },
    {
      label: 'INN SL Coinsurance',
      present: anyClass(
        innCls,
        (cls) => isPresent(cls?.coInsurance) || isPresent(cls?.patient)
      ),
    },
    {
      label: 'INN SL Family Copay',
      present: anyClass(innCls, (cls) => isPresent(cls?.familyCoPay)),
    },
    {
      label: 'INN SL Family Max Days',
      present: anyClass(innCls, (cls) => isPresent(cls?.familyMaxDays)),
    },
    {
      label: 'INN SL Family Coinsurance',
      present: anyClass(innCls, (cls) => isPresent(cls?.familyCoInsurance)),
    },

    {
      label: 'OON SL Copay',
      present: anyClass(
        oonCls,
        (cls) => isPresent(cls?.coPay) || isPresent(cls?.perDayVisit)
      ),
    },
    {
      label: 'OON SL Max Days',
      present: anyClass(oonCls, (cls) => isPresent(cls?.maxDays)),
    },
    {
      label: 'OON SL Coinsurance',
      present: anyClass(
        oonCls,
        (cls) => isPresent(cls?.coInsurance) || isPresent(cls?.patient)
      ),
    },
    {
      label: 'OON SL Family Copay',
      present: anyClass(oonCls, (cls) => isPresent(cls?.familyCoPay)),
    },
    {
      label: 'OON SL Family Max Days',
      present: anyClass(oonCls, (cls) => isPresent(cls?.familyMaxDays)),
    },
    {
      label: 'OON SL Family Coinsurance',
      present: anyClass(oonCls, (cls) => isPresent(cls?.familyCoInsurance)),
    },
  ];

  const missing = fields
    .filter((field) => !field.present)
    .map((field) => field.label);
  const filled = fields.length - missing.length;
  return {
    compScore: Math.round((filled * 100) / SCORED_FIELD_COUNT),
    missingFields: missing.length ? missing.join(', ') : null,
  };
};

export const calculateCompScore = (vob: any = {}): number =>
  calculateCompleteness(vob).compScore;
